import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface AssessmentRow {
  readonly assessmentId: string;
  readonly assessmentCode: string;
  readonly changeId: string;
  readonly businessImpact: string;
  readonly technicalImpact: string;
  readonly securityImpact: string;
  readonly rollbackComplexity: string;
  readonly riskRating: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'COMPLETED'];
const RISKS = ['', 'LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const IMPACTS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const COMPLEXITY = ['LOW', 'MEDIUM', 'HIGH'];

/**
 * M32 — Change Assessment Screen (§31 / §9). Register of change-impact
 * assessments showing the five impact axes, rollback complexity and the derived
 * overall risk rating (max-of-axes). Supports authoring an assessment against a
 * change and completing it (locking the derived risk for the §22 approval
 * workflow), each wired to the guarded /v1/devops/change-assessments write API.
 * Filterable by status and risk rating.
 */
export function ChangeAssessmentPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<AssessmentRow[]>([]);
  const [status, setStatus] = useState('');
  const [riskRating, setRiskRating] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newChangeId, setNewChangeId] = useState('');
  const [newBusiness, setNewBusiness] = useState('MEDIUM');
  const [newTechnical, setNewTechnical] = useState('MEDIUM');
  const [newSecurity, setNewSecurity] = useState('LOW');
  const [newRollback, setNewRollback] = useState('LOW');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (riskRating) params.set('riskRating', riskRating);
    const qs = params.toString();
    apiRequest<{ data: AssessmentRow[] }>(`/v1/devops/change-assessments${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, riskRating, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m32.changeAssess.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newChangeId.trim()) { setActionError(t('m32.changeAssess.changeIdRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/change-assessments`, {
        method: 'POST',
        body: {
          changeId: newChangeId.trim(),
          businessImpact: newBusiness,
          technicalImpact: newTechnical,
          securityImpact: newSecurity,
          rollbackComplexity: newRollback,
        },
        token,
      });
      setNotice(t('m32.changeAssess.created'));
      setNewChangeId('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const complete = async (r: AssessmentRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/change-assessments/${r.assessmentId}/complete`, { method: 'POST', body: {}, token });
      setNotice(t('m32.changeAssess.completedNotice', { code: r.assessmentCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="change-assessment">
      <h1>{t('m32.changeAssess.title')}</h1>

      <section aria-label="create-assessment">
        <h2>{t('m32.changeAssess.createTitle')}</h2>
        <form aria-label="assessment-form" onSubmit={(e) => void create(e)}>
          <label>{t('m32.changeAssess.fieldChangeId')}<input aria-label="field-change-id" value={newChangeId} onChange={(e) => setNewChangeId(e.target.value)} /></label>
          <label>{t('m32.changeAssess.fieldBusiness')}
            <select aria-label="field-business-impact" value={newBusiness} onChange={(e) => setNewBusiness(e.target.value)}>
              {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label>{t('m32.changeAssess.fieldTechnical')}
            <select aria-label="field-technical-impact" value={newTechnical} onChange={(e) => setNewTechnical(e.target.value)}>
              {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label>{t('m32.changeAssess.fieldSecurity')}
            <select aria-label="field-security-impact" value={newSecurity} onChange={(e) => setNewSecurity(e.target.value)}>
              {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </label>
          <label>{t('m32.changeAssess.fieldRollback')}
            <select aria-label="field-rollback-complexity" value={newRollback} onChange={(e) => setNewRollback(e.target.value)}>
              {COMPLEXITY.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-assessment" disabled={busy}>{busy ? t('m32.changeAssess.creating') : t('m32.changeAssess.createBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="assessment-error">{actionError}</p>}
        {notice && <p aria-label="assessment-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m32.changeAssess.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m32.changeAssess.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m32.changeAssess.riskLabel')}
          <select aria-label="filter-risk" value={riskRating} onChange={(e) => setRiskRating(e.target.value)}>
            {RISKS.map((r) => <option key={r || 'all'} value={r}>{r || t('m32.changeAssess.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m32.changeAssess.loading')}</p> : (
        <>
          <p aria-label="assessment-count">{t('m32.changeAssess.count', { count: rows.length })}</p>
          <table aria-label="assessment-table">
            <thead><tr><th>{t('m32.changeAssess.thCode')}</th><th>{t('m32.changeAssess.fieldBusiness')}</th><th>{t('m32.changeAssess.fieldTechnical')}</th><th>{t('m32.changeAssess.fieldSecurity')}</th><th>{t('m32.changeAssess.fieldRollback')}</th><th>{t('m32.changeAssess.riskLabel')}</th><th>{t('m32.changeAssess.status')}</th><th>{t('m32.changeAssess.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.assessmentId} data-risk={r.riskRating}>
                  <td>{r.assessmentCode}</td><td>{r.businessImpact}</td><td>{r.technicalImpact}</td>
                  <td>{r.securityImpact}</td><td>{r.rollbackComplexity}</td><td>{r.riskRating}</td><td>{r.status}</td>
                  <td>
                    {r.status === 'DRAFT' && (
                      <button type="button" aria-label={`complete-${r.assessmentCode}`} onClick={() => void complete(r)}>{t('m32.changeAssess.complete')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
